import type { ReactNode } from 'react';
import { Check, Minus, Pause, X } from 'lucide-react';
import {
  StageStatus,
  type AgentRole as AgentRoleT,
  type StageAttemptDto,
  type StageDto,
} from '@aiai/shared';
import { StatusBadge } from './StatusBadge';
import type { Tone } from './Badge';
import { isPulsing, STAGE_STATUS_TONE } from '../lib/statusTones';
import { formatCost, formatDuration, titleCase } from '../lib/format';

/**
 * Node colors derive from the shared per-status tone map, so the stepper and
 * the status badges can never disagree about what a stage state looks like.
 */
const TONE_DOT: Record<Tone, string> = {
  neutral: 'border-line-strong bg-surface-3 text-ink-faint',
  brand: 'border-signal bg-ink/20 text-signal',
  success: 'border-success-500/50 bg-success-500/20 text-success-300',
  warning: 'border-warning-500/70 bg-warning-500/20 text-warning',
  danger: 'border-danger-500/50 bg-danger-500/20 text-danger-300',
  info: 'border-info-500/50 bg-info-500/20 text-info-300',
};

const GLYPH: Record<string, ReactNode> = {
  [StageStatus.Succeeded]: <Check className="h-3.5 w-3.5" aria-hidden="true" />,
  [StageStatus.Failed]: <X className="h-3.5 w-3.5" aria-hidden="true" />,
  [StageStatus.Skipped]: <Minus className="h-3.5 w-3.5" aria-hidden="true" />,
  [StageStatus.AwaitingGate]: (
    <Pause className="h-3.5 w-3.5" aria-hidden="true" />
  ),
};

/**
 * Vertical stepper for a run's pipeline. Accepts live stage DTOs; when only the
 * planned roles are known (no run yet) pass `plannedRoles` to render a preview.
 * Pass `attempts` to show loop re-open reasons beneath failed non-final iterations.
 */
export function PipelineStepper({
  stages,
  plannedRoles,
  attempts,
}: {
  stages?: StageDto[];
  plannedRoles?: AgentRoleT[];
  attempts?: StageAttemptDto[];
}) {
  const items: Array<{
    key: string;
    role: AgentRoleT;
    order: number;
    status: StageStatus;
    stage?: StageDto;
  }> =
    stages && stages.length > 0
      ? [...stages]
          .sort((a, b) => a.order - b.order)
          .map((s) => ({
            key: s.id,
            role: s.role,
            order: s.order,
            status: s.status,
            stage: s,
          }))
      : (plannedRoles ?? []).map((role, i) => ({
          key: `${role}-${i}`,
          role,
          order: i + 1,
          status: StageStatus.Pending,
        }));

  if (items.length === 0) {
    return <p className="text-sm text-ink-faint">No pipeline stages.</p>;
  }

  // Build a map from stageOrder → non-final failed attempts with a loopReason,
  // so we can display re-open reasons beneath the relevant stage row.
  const loopReasonsByOrder = new Map<number, StageAttemptDto[]>();
  if (attempts) {
    for (const a of attempts) {
      if (a.loopReason && a.gatePassed === false) {
        const list = loopReasonsByOrder.get(a.order) ?? [];
        list.push(a);
        loopReasonsByOrder.set(a.order, list);
      }
    }
  }

  return (
    <ol className="relative space-y-0">
      {items.map((item, i) => {
        const failedAttempts = loopReasonsByOrder.get(item.order) ?? [];
        return (
          <li key={item.key} className="relative flex gap-3 pb-5 last:pb-0">
            {i < items.length - 1 && (
              <span
                className="absolute left-[13px] top-7 h-[calc(100%-1rem)] w-px bg-line-strong"
                aria-hidden="true"
              />
            )}
            <span
              className={`z-10 grid h-7 w-7 shrink-0 place-items-center rounded-full border text-xs font-semibold ${
                TONE_DOT[STAGE_STATUS_TONE[item.status]]
              }${isPulsing(item.status) ? ' animate-pulse-soft' : ''}`}
            >
              {GLYPH[item.status] ?? item.order}
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-ink">
                  {titleCase(item.role)}
                </span>
                <StatusBadge status={item.status} size="sm" />
                {item.stage?.costUsd != null && (
                  <span className="text-2xs text-ink-faint">
                    {formatCost(item.stage.costUsd)}
                  </span>
                )}
                {item.stage?.startedAt && (
                  <span className="text-2xs text-ink-faint">
                    {formatDuration(item.stage.startedAt, item.stage.finishedAt)}
                  </span>
                )}
              </div>
              {item.stage?.summary && (
                <p className="mt-1 text-xs leading-relaxed text-ink-muted">
                  {item.stage.summary}
                </p>
              )}
              {failedAttempts.map((a) => (
                <p
                  key={a.id}
                  className="mt-1 truncate text-2xs text-ink-faint"
                  title={a.loopReason ?? undefined}
                >
                  <span className="font-medium text-warning">Re-loop</span>
                  {' · '}
                  {a.loopReason}
                </p>
              ))}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
